/**
 * What the selected agent can reach while it works (task 796).
 *
 * Three chip groups — registry tools, MCP connections, knowledge bases — and one
 * line of truth about whether this flow actually loads any of them.
 *
 * That last line is the point of the panel. The engine gates ALL tool loading
 * behind the node's `autonomy.allowDataRequests`
 * (`collaborationNodeExecutors.ts` — registry tools, MCP tools and knowledge
 * tools are all inside that branch). An agent can therefore hold tools, MCP
 * connections and knowledge bases that a given flow never loads, and until now
 * nothing in the app said so. Note the field is `allowDataRequests`; the task
 * text calls it `dataRequests`, which is not the name in the definition type.
 *
 * Registry-tool chips do not link anywhere: tool names are a fixed engine
 * registry with no management page in this app (the report's "tool-catalog
 * endpoint" is listed as a not-yet-built backend nicety). Tools are edited on
 * the agent, which the identity header already links to. MCP and knowledge-base
 * chips do have detail pages, and link to them.
 *
 * Task 1309: every chip carries the same status dot — filled green means
 * enabled on this agent — with a legend under the groups, so Tools, MCP
 * connections and Knowledge bases share one indicator (report revision 3). The
 * dot replaced the MCP chip's old connection-active dot; a connection that is
 * itself switched off says so in words beside its name instead. `nodes` is
 * optional: outside a flow (the Agents page) there is no node to ask, so the
 * data-requests note becomes a pointer to Used by.
 *
 * Editing (task 1310, moved from `EditableCompactConfig`): the Tools group
 * shows the whole registry as on/off chips (hollow dot = available, not
 * enabled) whose toggles go to `onFieldChange('tools', …)` and save with the
 * agent; MCP connections and knowledge bases show what is attached, with ✕
 * calling the existing remove mutation and + Add / + Attach opening the existing
 * selector modals — those save immediately, as they always have. An agent that
 * does not exist yet (no id: the create page) cannot attach either, and the
 * groups say so. `innovate.fe.knowledge.agent-attachment` gates the knowledge
 * group in editing, as it gated the old Overview tab's section.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAgentMcpConnections, useRemoveAgentMcpConnection } from '@/hooks/useAgents';
import { useAgentKnowledgeBases, useRemoveAgentKnowledgeBase } from '@/hooks/useKnowledge';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useOptionalToast } from '@/contexts/ToastContext';
import { AgentMcpConnectionSelector } from '@/components/agents/AgentMcpConnectionSelector';
import { AgentKnowledgeBaseSelector } from '@/components/agents/AgentKnowledgeBaseSelector';
import { useMcpConnectionTools } from '@/hooks/useMcpConnections';
import type { Agent, McpConnection } from '@/types';
import type { AgentNode } from '@/types/definition';

const chipClasses =
  'inline-flex items-center gap-2 rounded-md border border-green-300 bg-green-50 px-2.5 py-1 text-xs text-gray-700 dark:border-green-800 dark:bg-green-900/20 dark:text-gray-200';

/**
 * The one capability indicator (task 1309): filled green = enabled on this
 * agent, hollow grey = available, not enabled.
 */
export function CapabilityStatusDot({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden="true"
      data-testid="capability-status-dot"
      data-state={on ? 'on' : 'off'}
      className={`inline-block h-2 w-2 flex-none rounded-full border ${
        on ? 'border-green-500 bg-green-500' : 'border-gray-400 bg-transparent'
      }`}
    />
  );
}

/** The engine's tool registry, as the agent form and inline editor have always offered it. */
export const AVAILABLE_TOOLS = [
  { value: 'file.read', label: 'File Read', description: 'Read files from data directory' },
  { value: 'file.write', label: 'File Write', description: 'Write files to data directory' },
  { value: 'http.fetch', label: 'HTTP Fetch', description: 'Fetch data from HTTP APIs' },
  { value: 'create.getProject', label: 'Get Project', description: 'Get project from Create API' },
  { value: 'create.listProjects', label: 'List Projects', description: 'List projects from Create API' },
];

const offChipClasses =
  'inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 bg-transparent px-2.5 py-1 text-xs text-gray-500 dark:border-gray-600 dark:text-gray-400';

const groupLabelClasses =
  'text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400';

/**
 * One MCP connection chip. Split out because the live tool count comes from the
 * per-connection discovery endpoint, which means one query per connection.
 */
function McpConnectionChip({ connection }: { connection: McpConnection }) {
  const { data, isLoading, isError } = useMcpConnectionTools(connection.id, connection.isActive);

  const count = isError
    ? 'tools unavailable'
    : isLoading
      ? 'counting…'
      : data
        ? `${data.toolCount} ${data.toolCount === 1 ? 'tool' : 'tools'}`
        : null;

  return (
    <span className={chipClasses} data-testid={`mcp-chip-${connection.id}`}>
      <CapabilityStatusDot on />
      <Link
        to={`/mcp-connections/${connection.id}`}
        className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
      >
        {connection.name}
      </Link>
      {count && <span className="text-gray-500 dark:text-gray-400">{count}</span>}
      {!connection.isActive && (
        <span className="text-gray-500 dark:text-gray-400">connection off</span>
      )}
    </span>
  );
}

interface AgentCapabilitiesPanelProps {
  agent: Agent;
  /**
   * Every node in this flow that runs the agent. Absent outside a flow (the
   * Agents page): loading is then decided per service, which the note says.
   */
  nodes?: AgentNode[];
  /** Render the editing variant (task 1310). The page passes the edited agent as `agent`. */
  editing?: boolean;
  /** Called on each tool toggle; the page accumulates these into its changed fields. */
  onFieldChange?: (field: string, value: unknown) => void;
}

const addButtonClasses =
  'inline-flex items-center gap-0.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300';

/** The legend line, one or two entries (task 1309 view, task 1310 editing). */
function CapabilityLegend({ withOff }: { withOff: boolean }) {
  return (
    <p
      data-testid="capability-legend"
      className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400"
    >
      <span className="inline-flex items-center gap-1.5">
        <CapabilityStatusDot on /> enabled on this agent
      </span>
      {withOff && (
        <span className="inline-flex items-center gap-1.5">
          <CapabilityStatusDot on={false} /> available, not enabled
        </span>
      )}
    </p>
  );
}

function AgentCapabilitiesEditor({
  agent,
  onFieldChange,
}: {
  agent: Agent;
  onFieldChange?: (field: string, value: unknown) => void;
}) {
  const exists = !!agent.id;
  const { data: mcpLinks } = useAgentMcpConnections(exists ? agent.id : undefined);
  const { data: knowledgeLinks } = useAgentKnowledgeBases(exists ? agent.id : undefined);
  const removeMcp = useRemoveAgentMcpConnection();
  const removeKnowledge = useRemoveAgentKnowledgeBase();
  const knowledgeEnabled = useFlag('innovate.fe.knowledge.agent-attachment');
  const toast = useOptionalToast();
  const [mcpSelectorOpen, setMcpSelectorOpen] = useState(false);
  const [knowledgeSelectorOpen, setKnowledgeSelectorOpen] = useState(false);

  const tools = agent.tools ?? [];
  // A tool the registry no longer lists still shows, enabled, so a toggle
  // elsewhere never silently drops it.
  const unknownTools = tools.filter((tool) => !AVAILABLE_TOOLS.some((entry) => entry.value === tool));

  const toggleTool = (tool: string) => {
    const next = tools.includes(tool) ? tools.filter((t) => t !== tool) : [...tools, tool];
    onFieldChange?.('tools', next);
  };

  return (
    <section
      data-testid="agent-capabilities-panel"
      data-editing="true"
      className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Capabilities</h3>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        What this agent can reach while it works
      </p>

      <div className="mt-4">
        <span className={groupLabelClasses}>Tools</span>
        <div className="mt-1.5 flex flex-wrap gap-2" data-testid="agent-tool-chips">
          {AVAILABLE_TOOLS.map((tool) => {
            const on = tools.includes(tool.value);
            return (
              <button
                key={tool.value}
                type="button"
                aria-pressed={on}
                title={tool.description}
                data-testid={`agent-tool-toggle-${tool.value}`}
                onClick={() => toggleTool(tool.value)}
                className={`${on ? chipClasses : offChipClasses} cursor-pointer`}
              >
                <CapabilityStatusDot on={on} />
                {tool.label}
              </button>
            );
          })}
          {unknownTools.map((tool) => (
            <button
              key={tool}
              type="button"
              aria-pressed
              data-testid={`agent-tool-toggle-${tool}`}
              onClick={() => toggleTool(tool)}
              className={`${chipClasses} cursor-pointer font-mono`}
            >
              <CapabilityStatusDot on />
              {tool}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between">
          <span className={groupLabelClasses}>MCP connections</span>
          {exists && (
            <button
              type="button"
              onClick={() => setMcpSelectorOpen(true)}
              data-testid="agent-mcp-add"
              className={addButtonClasses}
            >
              <PlusIcon className="h-3.5 w-3.5" /> Add
            </button>
          )}
        </div>
        {!exists ? (
          <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
            Attach connections after the agent is created.
          </p>
        ) : (mcpLinks ?? []).length === 0 ? (
          <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">No MCP connections attached.</p>
        ) : (
          <div className="mt-1.5 flex flex-wrap gap-2">
            {(mcpLinks ?? []).map((link) => (
              <span key={link.id} className={chipClasses} data-testid={`mcp-chip-${link.mcpConnectionId}`}>
                <CapabilityStatusDot on />
                {link.mcpConnection?.name ?? 'Unknown'}
                <button
                  type="button"
                  aria-label={`Remove ${link.mcpConnection?.name ?? 'connection'}`}
                  data-testid={`agent-mcp-remove-${link.mcpConnectionId}`}
                  disabled={removeMcp.isPending}
                  onClick={() =>
                    removeMcp.mutate(
                      { agentId: agent.id, mcpConnectionId: link.mcpConnectionId },
                      { onError: () => toast?.error('Could not remove the connection', 'Please try again.') }
                    )
                  }
                  className="text-gray-400 hover:text-red-600 disabled:opacity-50 dark:hover:text-red-400"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {knowledgeEnabled && (
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <span className={groupLabelClasses}>Knowledge bases</span>
            {exists && (
              <button
                type="button"
                onClick={() => setKnowledgeSelectorOpen(true)}
                data-testid="agent-knowledge-attach"
                className={addButtonClasses}
              >
                <PlusIcon className="h-3.5 w-3.5" /> Attach
              </button>
            )}
          </div>
          {!exists ? (
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              Attach knowledge bases after the agent is created.
            </p>
          ) : (knowledgeLinks ?? []).length === 0 ? (
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">No knowledge bases attached.</p>
          ) : (
            <div className="mt-1.5 flex flex-wrap gap-2">
              {(knowledgeLinks ?? []).map((link) => (
                <span key={link.id} className={chipClasses} data-testid={`knowledge-chip-${link.knowledgeBaseId}`}>
                  <CapabilityStatusDot on />
                  {link.knowledgeBase?.name ?? 'Unknown'}
                  <button
                    type="button"
                    aria-label={`Detach ${link.knowledgeBase?.name ?? 'knowledge base'}`}
                    data-testid={`agent-knowledge-remove-${link.knowledgeBaseId}`}
                    disabled={removeKnowledge.isPending}
                    onClick={() =>
                      removeKnowledge.mutate(
                        { agentId: agent.id, knowledgeBaseId: link.knowledgeBaseId },
                        { onError: () => toast?.error('Could not detach the knowledge base', 'Please try again.') }
                      )
                    }
                    className="text-gray-400 hover:text-red-600 disabled:opacity-50 dark:hover:text-red-400"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <CapabilityLegend withOff />

      <p className="mt-4 border-t border-gray-100 pt-3 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
        Tool toggles save with the agent. Adding or removing a connection or knowledge base saves
        immediately.
      </p>

      {exists && (
        <>
          <AgentMcpConnectionSelector
            agentId={agent.id}
            isOpen={mcpSelectorOpen}
            onClose={() => setMcpSelectorOpen(false)}
          />
          {knowledgeEnabled && (
            <AgentKnowledgeBaseSelector
              agentId={agent.id}
              isOpen={knowledgeSelectorOpen}
              onClose={() => setKnowledgeSelectorOpen(false)}
            />
          )}
        </>
      )}
    </section>
  );
}

export function AgentCapabilitiesPanel({
  agent,
  nodes,
  editing = false,
  onFieldChange,
}: AgentCapabilitiesPanelProps) {
  if (editing) {
    return <AgentCapabilitiesEditor agent={agent} onFieldChange={onFieldChange} />;
  }
  return <AgentCapabilitiesView agent={agent} nodes={nodes} />;
}

function AgentCapabilitiesView({ agent, nodes }: Pick<AgentCapabilitiesPanelProps, 'agent' | 'nodes'>) {
  const { data: mcpLinks } = useAgentMcpConnections(agent.id);
  const { data: knowledgeLinks } = useAgentKnowledgeBases(agent.id);

  const connections = (mcpLinks ?? [])
    .map((link) => link.mcpConnection)
    .filter((connection): connection is McpConnection => !!connection);

  const knowledgeBases = (knowledgeLinks ?? [])
    .map((link) => link.knowledgeBase)
    .filter((base): base is NonNullable<typeof base> => !!base);

  const inFlow = nodes !== undefined;
  const flowNodes = nodes ?? [];
  const allowingNodes = flowNodes.filter((node) => node.autonomy?.allowDataRequests === true);
  const allAllow = flowNodes.length > 0 && allowingNodes.length === flowNodes.length;
  const noneAllow = allowingNodes.length === 0;
  const hasCapabilities =
    agent.tools.length > 0 || connections.length > 0 || knowledgeBases.length > 0;

  return (
    <section
      data-testid="agent-capabilities-panel"
      className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Capabilities</h3>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        What this agent can reach while it works
      </p>

      {!hasCapabilities && (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          This agent has no tools, MCP connections, or knowledge bases attached — it works from its
          prompt and input alone.
        </p>
      )}

      {agent.tools.length > 0 && (
        <div className="mt-4">
          <span className={groupLabelClasses}>Tools</span>
          <div className="mt-1.5 flex flex-wrap gap-2" data-testid="agent-tool-chips">
            {agent.tools.map((tool) => (
              <span key={tool} className={`${chipClasses} font-mono`}>
                <CapabilityStatusDot on />
                {tool}
              </span>
            ))}
          </div>
        </div>
      )}

      {connections.length > 0 && (
        <div className="mt-4">
          <span className={groupLabelClasses}>MCP connections</span>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {connections.map((connection) => (
              <McpConnectionChip key={connection.id} connection={connection} />
            ))}
          </div>
        </div>
      )}

      {knowledgeBases.length > 0 && (
        <div className="mt-4">
          <span className={groupLabelClasses}>Knowledge bases</span>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {knowledgeBases.map((base) => (
              <span key={base.id} className={chipClasses}>
                <CapabilityStatusDot on />
                <Link
                  to={`/knowledge-bases/${base.id}`}
                  className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  {base.name}
                </Link>
                {typeof base.documentCount === 'number' && (
                  <span className="text-gray-500 dark:text-gray-400">
                    {base.documentCount} {base.documentCount === 1 ? 'doc' : 'docs'}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {hasCapabilities && <CapabilityLegend withOff={false} />}

      <p
        data-testid="agent-data-requests-note"
        className="mt-4 border-t border-gray-100 pt-3 text-xs dark:border-gray-700"
      >
        {!inFlow ? (
          <span className="text-gray-500 dark:text-gray-400">
            Loading is decided per service by each flow&rsquo;s node — see Used by.
          </span>
        ) : noneAllow ? (
          <span className="text-amber-700 dark:text-amber-300">
            This flow does not permit data requests — the tools, connections, and knowledge above are
            not loaded when this service runs.
          </span>
        ) : allAllow ? (
          <span className="text-gray-500 dark:text-gray-400">
            <span className="font-semibold text-green-700 dark:text-green-400">
              Data requests allowed
            </span>{' '}
            in this flow — the capabilities above are loaded at run time.
          </span>
        ) : (
          <span className="text-amber-700 dark:text-amber-300">
            Data requests are permitted at some of this agent&rsquo;s nodes but not others (
            {allowingNodes.map((node) => node.id).join(', ')}) — the capabilities above load only at
            those nodes.
          </span>
        )}
      </p>
    </section>
  );
}

export default AgentCapabilitiesPanel;
